/**
 * Code-Defined Feature Registry
 *
 * This is the single source of truth for what features exist in the platform.
 * The database (features table) is synced FROM this registry on API startup.
 * The admin UI can toggle features on/off, but cannot create or delete them.
 *
 * To add a new feature:
 *   1. Add it here
 *   2. The next API server restart will sync it to the database
 *   3. It will automatically be included in trial bundles
 */
/**
 * All platform features, keyed by their unique feature key.
 * Feature keys are used in code for gating (e.g., requireFeature('expense_tracking')).
 */
export const FEATURE_REGISTRY = {
    base: {
        name: 'Base Platform',
        description: 'Property and unit management, tenant management, lease management',
    },
    payments: {
        name: 'Rent Payments',
        description: 'Collect rent payments from tenants via ACH',
    },
    chat: {
        name: 'Messaging',
        description: 'SMS and email communication with tenants',
    },
    expense_tracking: {
        name: 'Expense Tracking',
        description: 'AI-powered receipt scanning, categorization, and IRS tax categories',
    },
    documents: {
        name: 'Document Management',
        description: 'Lease generation, built-in e-signatures, rules & regulations',
    },
    maintenance: {
        name: 'Maintenance Tracking',
        description: 'Appliance tracking, paint log, capital improvements',
    },
    reporting: {
        name: 'Reports & Analytics',
        description: 'Income, expense, and tax reports',
    },
};
/** Array of all feature keys (useful for iteration) */
export const ALL_FEATURE_KEYS = Object.keys(FEATURE_REGISTRY);
/**
 * Capabilities that are DERIVED at resolution time (saas-subscription Iteration 16), never
 * stored on a bundle: `agentic_support` from the agent add-on status, `leasing_pipeline` from
 * the entitling subscription alone (BR-97, Iteration 18). They are no longer in
 * FEATURE_REGISTRY, so they can never be added to a bundle template.
 */
export const DERIVED_CAPABILITY_KEYS = ['agentic_support', 'leasing_pipeline'];
/**
 * Keys removed from the bundle vocabulary. Stripped from stored bundle features on read and
 * from template input on write (never an error — legacy snapshots may still carry them).
 */
export const RETIRED_FEATURE_KEYS = ['agentic_support', 'leasing_pipeline', 'listing_support'];
/** Every key an entitlement check may ask about: stored feature keys plus derived capabilities. */
export const ALL_ENTITLEMENT_KEYS = [...ALL_FEATURE_KEYS, ...DERIVED_CAPABILITY_KEYS];
//# sourceMappingURL=features.js.map